import React from 'react';
import ReactDOM from 'react-dom';
import * as Sentry from '@sentry/react';
import './index.css';
import TranscriptionInterface from './App';
import PublicSharePage from './components/PublicSharePage';
import PublicShell from './components/public/PublicShell';
import ClientAuthPage from './components/public/ClientAuthPage';
import ClientLanding, { preloadLanding } from './components/public/ClientLanding';
import LoginLinkPage, { isLoginLinkPath } from './components/public/LoginLinkPage';
import { isClientHost } from './lib/host';
import { authService } from './services/authService';
import { publicPage, preloadPublicPage } from './components/public/publicRoutes';
import { initKeycloak } from './keycloak';
import { applyBrand, PRODUCT_NAME } from './config/branding';
import { sentryOptions } from './lib/sentryOptions';

// Sentry фронта выключен 24.09.2026 (решение владельца): события уходят
// в ingest.de.sentry.io, то есть за пределы РФ, а оферта, согласие и политика
// обещают обработку в России. Включается только явным флагом — и тогда в
// документах нужно описать трансграничную передачу.
if (process.env.REACT_APP_SENTRY_DSN && process.env.REACT_APP_SENTRY_ENABLED === 'true') {
  Sentry.init(sentryOptions());
}

// Соломка на окно деплоя: если вкладка открыта во время выката и браузер по
// старому index.html просит чанк, которого уже нет (ChunkLoadError) — один раз
// перезагружаем страницу, чтобы подтянуть свежий index.html с актуальными хешами.
// sessionStorage-флаг защищает от петли перезагрузок.
const isChunkError = (msg) => /Loading chunk [^\s]+ failed|ChunkLoadError|Loading CSS chunk/i.test(msg || '');
const reloadOnce = () => {
  if (sessionStorage.getItem('chunk-reloaded')) return;
  sessionStorage.setItem('chunk-reloaded', '1');
  window.location.reload();
};
window.addEventListener('error', (e) => { if (isChunkError(e?.message)) reloadOnce(); });
window.addEventListener('unhandledrejection', (e) => { if (isChunkError(e?.reason?.message)) reloadOnce(); });

const shareMatch = window.location.pathname.match(/^\/s\/([\w-]+)\/?$/);
// Оферта и согласие на обработку данных — статические документы, у них нет ни
// пользователя, ни токена. Рендерим их сразу, не дожидаясь initKeycloak: недоступный
// или медленный SSO не должен превращать юридический документ в пустую страницу.
const publicDoc = publicPage(window.location.pathname);

// Цвета бренда — в корень документа до первого рендера, иначе первый кадр уедет
// с дефолтом и мигнёт чужим цветом.
applyBrand();

const mount = (node) => ReactDOM.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary fallback={<p>Произошла ошибка. Попробуйте обновить страницу.</p>}>
      {node}
    </Sentry.ErrorBoundary>
  </React.StrictMode>,
  document.getElementById('root')
);

// После входа по коду перезагружаем страницу: дальше работает обычное
// приложение, которое читает ключ доступа из localStorage при старте.
const handleClientAuth = async (token) => {
  await authService.checkToken(token);
  window.location.reload();
};

// Вход по ссылке из приглашения — раньше всего остального, на любом домене:
// ссылка выдаёт обычный ключ доступа, Keycloak ей не нужен, а открытая ссылка
// должна войти под приглашённым, даже если во вкладке уже кто-то вошёл.
// После входа — replace на корень: /login-link не остаётся в истории, а
// приложение стартует обычным путём с ключом из localStorage.
const handleLinkAuth = async (token) => {
  await authService.checkToken(token);
  window.location.replace('/');
};

if (isLoginLinkPath(window.location.pathname)) {
  mount(<PublicShell><LoginLinkPage onAuth={handleLinkAuth} /></PublicShell>);
} else if (publicDoc) {
  // Страница уже на экране готовым HTML (scripts/prerender.js): сначала дочитываем
  // её код и текст, потом рисуем — иначе она мигнула бы пустотой.
  preloadPublicPage(window.location.pathname)
    .then(() => mount(<PublicShell>{publicDoc}</PublicShell>));
} else if (isClientHost() && !shareMatch) {
  // На клиентском домене Keycloak НЕ инициализируем НИКОГДА, а не только пока
  // нет ключа доступа. Этот origin не входит в разрешённые redirect URI клиента
  // newton_ui, silent check-sso уходит на страницу ошибки Keycloak и обратно не
  // возвращается, а keycloak-js на этот путь таймаут не ставит — initPromise не
  // разрешается, .finally() не срабатывает и страница остаётся пустой. Клиент,
  // только что вошедший по коду, получал бы белый экран после перезагрузки.
  if (authService.getToken()) {
    // Заголовок вкладки у готовой витрины длинный, под поиск; приложению — имя продукта.
    document.title = PRODUCT_NAME;
    mount(<TranscriptionInterface />);
  } else {
    // Ровно то же, что рендерит App.js на этом домене: витрина с клиентским
    // входом в слоте. Голая карточка в PublicShell растягивалась во всю ширину
    // экрана — ширину ей задаёт колонка витрины, а не она сама.
    preloadLanding().then(() => mount(
      <PublicShell>
        <ClientLanding authSlot={<ClientAuthPage onAuth={handleClientAuth} />} />
      </PublicShell>
    ));
  }
} else {
  initKeycloak().finally(() => {
    mount(shareMatch ? <PublicSharePage token={shareMatch[1]} /> : <TranscriptionInterface />);
  });
}
